import { Link, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useState } from "react";
import type { jsPDF as JsPDF } from "jspdf";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiMapPin,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { PiTicket } from "react-icons/pi";
import { useGetSingleTicketQuery } from "../../api/tickets";
import { MySingleTicketModel } from "../../types/tickets";

export default function MySingleTicket() {
  const { id } = useParams();
  const { data, isLoading } = useGetSingleTicketQuery({ id: id ?? "" });
  const [isDownloading, setIsDownloading] = useState(false);

  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-[#f7f4e9] px-5">
        <div className="h-72 w-full max-w-4xl animate-pulse rounded-lg bg-[#ddd6bd]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 bg-[#f7f4e9] px-5 text-center">
        <p className="text-2xl font-black text-[#06130a]">No ticket found.</p>
        <Link
          to="/tickets"
          className="rounded-md bg-[#9d1c1c] px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-white"
        >
          My tickets
        </Link>
      </div>
    );
  }

  const bookedAt = new Date(data.created_at);
  const eventDate = new Date(data.event.date);
  const scannedDate = data.scanned_at ? new Date(data.scanned_at) : null;
  const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(
    data.ticket_id
  )}&size=420&margin=1`;
  const isScanned = Boolean(data.scanned_at);

  return (
    <div className="min-h-screen bg-[#f7f4e9] text-[#06130a]">
      <section className="relative overflow-hidden bg-[#06130a] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: `url('${data.event.image}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06130a] via-[#08170d]/90 to-[#1c5228]/45" />

        <div className="relative mx-auto flex w-full max-w-[1300px] flex-col gap-6 px-5 py-10 md:px-12">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <Link
              to="/tickets"
              className="inline-flex w-fit items-center gap-2 text-sm font-black uppercase tracking-[0.14em] text-white/70 transition hover:text-[#e8b64c]"
            >
              <FiArrowLeft />
              My tickets
            </Link>

            <button
              type="button"
              disabled={isDownloading}
              onClick={async () => {
                setIsDownloading(true);
                try {
                  await downloadTicketPdf(data, qrUrl, {
                    bookedAt,
                    eventDate,
                    scannedDate,
                  });
                } catch (error) {
                  window.alert("Unable to download the ticket PDF. Please try again.");
                } finally {
                  setIsDownloading(false);
                }
              }}
              className="inline-flex w-fit items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-[#06130a] transition hover:bg-[#f6d98a] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiDownload />
              {isDownloading ? "Preparing..." : "Download PDF"}
            </button>
          </div>

          <div className="max-w-4xl">
            <p className="text-sm font-black uppercase tracking-[0.28em] text-[#e8b64c]">
              Ticket confirmation
            </p>
            <h1 className="mt-3 text-4xl font-black uppercase leading-tight text-[#f6d98a] md:text-6xl">
              {data.event.title}
            </h1>
            <div className="mt-5 flex flex-wrap gap-3 text-sm font-bold text-white/75">
              <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/10 px-3 py-2">
                <FiCalendar />
                {formatDate(eventDate)}
              </span>
              <span className="inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/10 px-3 py-2">
                <FiMapPin />
                {data.event.location}, {data.event.country}
              </span>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto grid w-full max-w-[1300px] grid-cols-1 gap-6 px-5 py-8 md:px-12 lg:grid-cols-[420px_1fr]">
        <aside className="h-fit overflow-hidden rounded-lg bg-[#06130a] p-5 text-white shadow-[0_24px_70px_rgba(18,2,2,0.16)]">
          <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#e8b64c]">
                Scan at entry
              </p>
              <p className="mt-1 text-lg font-black uppercase text-white">
                {data.pricing.type}
              </p>
            </div>
            <StatusBadge isScanned={isScanned} />
          </div>

          <div className="mt-5 rounded-lg bg-white p-4">
            <img
              src={qrUrl}
              alt={data.ticket_id}
              className="mx-auto aspect-square w-full max-w-[340px]"
            />
          </div>

          <div className="mt-5 rounded-lg border border-white/10 bg-white/[0.06] p-4">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-white/45">
              Ticket ID
            </p>
            <p className="mt-2 break-all font-mono text-sm text-white/85">
              {data.ticket_id}
            </p>
          </div>
        </aside>

        <section className="rounded-lg border border-[#ddd6bd] bg-white p-5 shadow-[0_24px_70px_rgba(18,2,2,0.08)] md:p-7">
          <div className="flex flex-col justify-between gap-4 border-b border-[#ddd6bd] pb-6 md:flex-row md:items-start">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-[#9d1c1c]">
                Ticket details
              </p>
              <h2 className="mt-2 text-3xl font-black uppercase text-[#06130a]">
                {data.pricing.type} Pass
              </h2>
            </div>

            <div className="rounded-lg bg-[#efe8cd] px-5 py-4 text-left md:text-right">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#143d20]/60">
                Price
              </p>
              <p className="mt-1 text-2xl font-black text-[#06130a]">
                {data.pricing.price.toLocaleString()} {data.event.currency}
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            <TicketField
              icon={<FiUser />}
              label="Ticket holder"
              value={data.names}
            />
            <TicketField
              icon={<PiTicket />}
              label="Ticket type"
              value={data.pricing.type}
            />
            <TicketField
              icon={<FiCalendar />}
              label="Booked date"
              value={formatDate(bookedAt)}
            />
            <TicketField
              icon={<FiClock />}
              label="Booked time"
              value={formatTime(bookedAt)}
            />
            <TicketField
              icon={<FiMapPin />}
              label="Venue"
              value={`${data.event.location}, ${data.event.country}`}
            />
            <TicketField
              icon={<FiShield />}
              label="Scan status"
              value={
                scannedDate ? `Scanned ${formatDate(scannedDate)}` : "Not scanned"
              }
              highlight={!scannedDate}
            />
          </div>

          <div className="mt-6 rounded-lg bg-[#06130a] p-5 text-white">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-[#e8b64c]">
                  Important
                </p>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-white/70">
                  Keep this QR code ready at the entrance. Each ticket can be
                  scanned once.
                </p>
              </div>
              {!isScanned && (
                <div className="inline-flex w-fit items-center gap-2 rounded-md bg-green-500/15 px-4 py-3 text-sm font-black uppercase tracking-[0.12em] text-green-300">
                  <FiCheckCircle />
                  Valid
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function TicketField({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex min-h-24 gap-4 rounded-lg border border-[#ddd6bd] bg-[#f7f4e9] p-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-[#9d1c1c] text-xl text-white">
        {icon}
      </div>
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-[#143d20]/55">
          {label}
        </p>
        <p
          className={`mt-2 text-lg font-black ${
            highlight ? "text-green-600" : "text-[#06130a]"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function StatusBadge({ isScanned }: { isScanned: boolean }) {
  return (
    <div
      className={`rounded-md px-3 py-2 text-xs font-black uppercase tracking-[0.14em] ${
        isScanned
          ? "bg-white/10 text-white/60"
          : "bg-green-500/15 text-green-300"
      }`}
    >
      {isScanned ? "Used" : "Valid"}
    </div>
  );
}

async function downloadTicketPdf(
  ticket: MySingleTicketModel,
  qrUrl: string,
  dates: {
    bookedAt: Date;
    eventDate: Date;
    scannedDate: Date | null;
  }
) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const dark = "#06130a";
  const orange = "#9d1c1c";
  const gold = "#e8b64c";
  const cream = "#f7f4e9";
  const line = "#ddd6bd";
  const ink = "#06130a";
  const cardX = margin;
  const cardY = 18;
  const cardW = pageWidth - margin * 2;
  const cardH = 158;
  const stubW = 86;
  const detailsX = cardX + stubW;
  const detailsW = cardW - stubW;
  const statusText = ticket.scanned_at ? "Used" : "Valid";
  const ticketTitle = titleCase(ticket.event.title);
  const ticketType = titleCase(ticket.pricing.type);
  const price = `${ticket.pricing.price.toLocaleString()} ${ticket.event.currency}`;
  const scanStatus = dates.scannedDate
    ? `Scanned ${formatDate(dates.scannedDate)}`
    : "Not scanned";

  pdf.setFillColor(cream);
  pdf.rect(0, 0, pageWidth, pageHeight, "F");

  pdf.setFillColor(dark);
  pdf.roundedRect(cardX, cardY, cardW, cardH, 6, 6, "F");
  pdf.setFillColor("#ffffff");
  pdf.roundedRect(detailsX, cardY, detailsW, cardH, 0, 6, "F");

  pdf.setFillColor(orange);
  pdf.rect(detailsX, cardY, 3, cardH, "F");
  pdf.setDrawColor("#5c2b28");
  pdf.setLineDashPattern([1.4, 2.6], 0);
  pdf.line(detailsX, cardY + 8, detailsX, cardY + cardH - 8);
  pdf.setLineDashPattern([], 0);

  pdf.setFillColor(cream);
  pdf.circle(detailsX, cardY, 6, "F");
  pdf.circle(detailsX, cardY + cardH, 6, "F");

  pdf.setTextColor(gold);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.text("IGITANGAZA", cardX + 12, cardY + 16);

  pdf.setTextColor("#ffffff");
  pdf.setFontSize(17);
  pdf.text(ticketType, cardX + 12, cardY + 29);

  pdf.setTextColor("#ffffff");
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.text("Scan this code at the entrance.", cardX + 12, cardY + 38);

  const qrDataUrl = await fetchImageDataUrl(qrUrl);
  pdf.setFillColor("#ffffff");
  pdf.roundedRect(cardX + 12, cardY + 48, 62, 62, 4, 4, "F");
  pdf.addImage(qrDataUrl, "PNG", cardX + 16, cardY + 52, 54, 54);

  pdf.setFillColor("#2b1010");
  pdf.roundedRect(cardX + 12, cardY + 119, 62, 23, 3, 3, "F");
  pdf.setTextColor("#b8988f");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(6.5);
  pdf.text("TICKET ID", cardX + 17, cardY + 128);
  pdf.setTextColor("#ffffff");
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7.8);
  pdf.text(pdf.splitTextToSize(ticket.ticket_id, 52), cardX + 17, cardY + 137);

  const contentX = detailsX + 13;
  const contentY = cardY + 15;

  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(orange);
  pdf.setFontSize(8);
  pdf.text("Ticket confirmation", contentX, contentY);

  pdf.setTextColor(ink);
  pdf.setFontSize(25);
  pdf.text(pdf.splitTextToSize(ticketTitle, 118), contentX, contentY + 15);

  pdf.setFillColor("#efe8cd");
  pdf.roundedRect(detailsX + detailsW - 62, contentY - 4, 47, 25, 4, 4, "F");
  pdf.setTextColor("#9c6258");
  pdf.setFontSize(6.5);
  pdf.text("PRICE", detailsX + detailsW - 38.5, contentY + 5, { align: "center" });
  pdf.setTextColor(ink);
  pdf.setFontSize(14);
  pdf.text(price, detailsX + detailsW - 38.5, contentY + 16, { align: "center" });

  pdf.setDrawColor(line);
  pdf.line(contentX, cardY + 58, detailsX + detailsW - 15, cardY + 58);

  drawPdfMiniField(pdf, contentX, cardY + 68, 56, "Holder", ticket.names);
  drawPdfMiniField(pdf, contentX + 66, cardY + 68, 42, "Ticket", ticketType);
  drawPdfMiniField(pdf, contentX + 118, cardY + 68, 45, "Status", statusText, !ticket.scanned_at);

  drawPdfMiniField(pdf, contentX, cardY + 101, 56, "Event date", formatDate(dates.eventDate));
  drawPdfMiniField(pdf, contentX + 66, cardY + 101, 42, "Venue", `${ticket.event.location}, ${ticket.event.country}`);
  drawPdfMiniField(pdf, contentX + 118, cardY + 101, 45, "Booked", `${formatDate(dates.bookedAt)}  ${formatTime(dates.bookedAt)}`);

  pdf.setFillColor(dark);
  pdf.roundedRect(contentX, cardY + 136, detailsW - 28, 24, 4, 4, "F");
  pdf.setTextColor(gold);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.text("Entry note", contentX + 6, cardY + 146);
  pdf.setTextColor("#ffffff");
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.text(
    pdf.splitTextToSize(
      `Keep this PDF or QR code ready. Each ticket can be scanned once. Current scan status: ${scanStatus}.`,
      detailsW - 42
    ),
    contentX + 6,
    cardY + 155
  );

  pdf.setTextColor("#7d6c64");
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text("Powered By Ishema Tickets", margin, pageHeight - 12);
  pdf.text("info@ishema.rw", pageWidth - margin, pageHeight - 12, { align: "right" });

  pdf.save(`${slugify(ticket.event.title)}-${ticket.pricing.type}-${ticket.ticket_id}.pdf`);
}

function drawPdfMiniField(
  pdf: JsPDF,
  x: number,
  y: number,
  width: number,
  label: string,
  value: string,
  highlight = false
) {
  pdf.setDrawColor("#ddd6bd");
  pdf.setFillColor("#f7f4e9");
  pdf.roundedRect(x, y, width, 23, 3, 3, "FD");
  pdf.setTextColor("#9c6258");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(6.5);
  pdf.text(label.toUpperCase(), x + 4, y + 7);
  pdf.setTextColor(highlight ? "#159947" : "#06130a");
  pdf.setFontSize(9.2);
  pdf.text(pdf.splitTextToSize(value, width - 8), x + 4, y + 16);
}

async function fetchImageDataUrl(url: string) {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Unable to fetch image");

  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function titleCase(value: string) {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(date: Date) {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(date: Date) {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}
